import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { useGetProject, useMarkProjectInitialized } from '@/stores/projectsStore';
import { getStartingAngleConfig, checkProjectInitialization } from '@/config/initializationConfig';

interface InitializationStatus {
    isInitialized: boolean;
    startingAngle: string | null;
    showInitializationMessage: boolean;
    initializationMessage: string;
    initializationSteps: string[];
    currentStep: number;
}

interface Project {
    id: string;
    projectName: string;
    startingAngle?: string;
    isInitialized?: boolean;
    nodes?: any[];
    edges?: any[];
}

export const useProjectInitialization = (currentNodes?: any[]) => {
    const { projectId } = useParams();

    const [initStatus, setInitStatus] = useState<InitializationStatus>({
        isInitialized: false,
        startingAngle: null,
        showInitializationMessage: false,
        initializationMessage: '',
        initializationSteps: [],
        currentStep: 0
    });

    const [stepIndex, setStepIndex] = useState(0);

    const getProject = useGetProject();
    const markProjectInitialized = useMarkProjectInitialized();

    // Use ref to avoid infinite dependency cycles
    const stepIndexRef = useRef(stepIndex); stepIndexRef.current = stepIndex;

    const checkAndUpdateInitialization = useCallback(async (project: Project, nodes?: any[]) => {

        if (!project) {

            return;
        }        // Use provided nodes or fallback to currentNodes
        const nodesToCheck = nodes || currentNodes;

        // Wait for nodes to be loaded before checking initialization
        if (!nodesToCheck || nodesToCheck.length === 0) {

            return;
        }

        // Get configuration for this starting angle
        const config = getStartingAngleConfig(project.startingAngle || 'blank');


        // Create a project object that uses ONLY current flow nodes for condition checking
        const projectWithCurrentNodes = {
            ...project,
            nodes: nodesToCheck
        };
        // Check if frontend conditions are met using the flexible configuration
        const conditionsMet = checkProjectInitialization(projectWithCurrentNodes);


        // Show welcome message if:
        // 1. Project is NOT marked as initialized in backend AND
        // 2. Frontend conditions are NOT met (user hasn't completed required actions)
        const shouldShowMessage = !project.isInitialized && !conditionsMet;
        // If frontend conditions are met but backend hasn't marked it yet, call backend
        if (conditionsMet && !project.isInitialized) {

            markProjectInitialized.execute({
                projectId: projectId!,
                onSuccess: () => {

                    setInitStatus({
                        isInitialized: true,
                        startingAngle: project.startingAngle || null,
                        showInitializationMessage: false,
                        initializationMessage: config.welcomeMessage,
                        initializationSteps: config.steps,
                        currentStep: 0
                    });
                }, onError: (_error) => {
                    // Still update UI state even if backend call fails
                    setInitStatus({
                        isInitialized: conditionsMet,
                        startingAngle: project.startingAngle || null,
                        showInitializationMessage: shouldShowMessage,
                        initializationMessage: config.welcomeMessage,
                        initializationSteps: config.steps,
                        currentStep: stepIndexRef.current
                    });
                }
            });
        } else {
            // Update UI state based on current conditions
            setInitStatus({
                isInitialized: project.isInitialized || conditionsMet,
                startingAngle: project.startingAngle || null,
                showInitializationMessage: shouldShowMessage,
                initializationMessage: config.welcomeMessage,
                initializationSteps: config.steps,
                currentStep: stepIndexRef.current
            });
        }
    }, [projectId, markProjectInitialized]); // Remove currentNodes from dependencies to prevent infinite loop// Check initialization on mount and when projectId changes
    useEffect(() => {
        if (!projectId) return;

        let mounted = true;

        const fetchProject = () => {
            getProject.execute({
                id: projectId, onSuccess: (response) => {
                    if (mounted) {

                        // The API returns { project: {...} }, so we need to access response.data.project
                        checkAndUpdateInitialization(response.data.project, currentNodes);
                    }
                },
                onError: (error) => {
                    if (mounted) {
                        console.error('Failed to fetch project for initialization check:', error);
                    }
                }
            });
        }; fetchProject();

        return () => {
            mounted = false;
        };
    }, [projectId]); // Removed getProject and checkAndUpdateInitialization from dependencies

    // Re-run initialization check when current nodes change (debounced to prevent infinite loops)
    useEffect(() => {
        if (!projectId || !currentNodes || currentNodes.length === 0) return;

        // Debounce the check to prevent infinite loops
        const timeoutId = setTimeout(() => {
            getProject.execute({
                id: projectId, onSuccess: (response) => {
                    checkAndUpdateInitialization(response.data.project, currentNodes);
                },
                onError: (error) => {
                    console.error('Failed to re-check initialization with current nodes:', error);
                }
            });
        }, 100); // Small delay to debounce

        return () => clearTimeout(timeoutId);
    }, [currentNodes?.length, projectId]); // Use length instead of the full array to reduce re-renders

    // Manual refresh function for when nodes change
    const refreshInitializationStatus = useCallback(() => {
        if (!projectId) return;

        getProject.execute({
            id: projectId, onSuccess: (response) => {
                // The API returns { project: {...} }, so we need to access response.data.project
                checkAndUpdateInitialization(response.data.project, currentNodes);
            },
            onError: (error) => {
                console.error('Failed to refresh project initialization status:', error);
            }
        });
    }, [projectId]); // Remove checkAndUpdateInitialization from dependencies

    const dismissInitializationMessage = useCallback(() => {
        setInitStatus(prev => ({ ...prev, showInitializationMessage: false }));
    }, []);

    const skipInitialization = useCallback(() => {
        if (!projectId) return;

        // Mark project as initialized in backend to permanently skip
        markProjectInitialized.execute({
            projectId: projectId,
            onSuccess: () => {
                setInitStatus({
                    isInitialized: true,
                    startingAngle: initStatus.startingAngle,
                    showInitializationMessage: false,
                    initializationMessage: initStatus.initializationMessage,
                    initializationSteps: initStatus.initializationSteps,
                    currentStep: initStatus.currentStep
                });
            },
            onError: (error) => {
                console.error('Failed to skip initialization:', error);
                // Still hide the message locally even if backend call fails
                setInitStatus(prev => ({ ...prev, showInitializationMessage: false }));
            }
        });
    }, [projectId, initStatus]);

    const nextStep = useCallback(() => {
        setStepIndex(prev => {
            const newIndex = Math.min(prev + 1, initStatus.initializationSteps.length - 1);
            setInitStatus(current => ({ ...current, currentStep: newIndex }));
            return newIndex;
        });
    }, [initStatus.initializationSteps.length]);

    const previousStep = useCallback(() => {
        setStepIndex(prev => {
            const newIndex = Math.max(prev - 1, 0);
            setInitStatus(current => ({ ...current, currentStep: newIndex }));
            return newIndex;
        });
    }, []);

    return {
        isInitialized: initStatus.isInitialized,
        startingAngle: initStatus.startingAngle,
        showInitializationMessage: initStatus.showInitializationMessage,
        initializationMessage: initStatus.initializationMessage,
        initializationSteps: initStatus.initializationSteps,
        currentStep: initStatus.currentStep,
        dismissInitializationMessage,
        skipInitialization,
        nextStep,
        previousStep,
        refreshInitializationStatus
    };
};
